/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import {
    Button,
    Heading,
    Icon,
    Text,
    ContentContainerBodyShift,
    useFlags,
} from 'sarsaparilla';
import { useWizardContext } from 'ui-interagency-pass/src/components/PassWizard/context/WizardContext';

import './WhichPass.scss';

function WhichPass() {
    const { releaseDigitalIapFeatures } = useFlags();
    const wizardEnabled = releaseDigitalIapFeatures?.helpMeChooseWizard ?? false;
    const { openWizard } = useWizardContext();

    return (
        <ContentContainerBodyShift className="my-6">
            <section aria-label="Which Pass Do I Need" id="which-pass">
                <Heading
                    headingLevel={2}
                    hasUnderline="left"
                    appearance="h4"
                    className="header"
                    id="skip-to-which-pass"
                >
                    Which Pass Do I Need?
                </Heading>

                <Text className="which-pass-info" tag="div">
                    Whether you&apos;re planning a single trip or coming back multiple
                    times a year, you can find the right pass for your visit on{' '}
                    {process.env.SITE_NAME}.
                    <ul aria-labelledby="skip-to-which-pass">
                        <li>
                            <strong>Eligible for a free or discounted pass?</strong> U.S.
                            citizens and residents may qualify for a free or discounted
                            America the Beautiful Pass if one of the following applies to
                            you:
                            <ul>
                                <li>Age 62 or older</li>
                                <li>Current U.S. military or dependent </li>
                                <li>Veteran or Gold Star Family member</li>
                                <li>4th grader</li>
                                <li>Have a permanent disability</li>
                                <li className="extra-info">
                                    The digital version of the free and discounted passes
                                    are now available on {process.env.SITE_NAME}.
                                </li>
                            </ul>
                        </li>
                        <li>
                            <strong>
                                Planning to visit more than one federal recreation
                                location this year?
                            </strong>{' '}
                            Purchase an America the Beautiful Annual Pass. Remember to
                            check if you or anyone in your party is eligible for a free or
                            discounted pass.
                        </li>
                        <li>
                            <strong>
                                Planning to visit just one location, one or more times?
                            </strong>{' '}
                            Purchase a
                            <a
                                href="#site-specific-digital-passes"
                                data-ga-tracking-id="054909975123"
                            >
                                {' '}
                                Site Pass
                            </a>{' '}
                            for that specific location.{' '}
                        </li>
                    </ul>
                </Text>
                <div className="table-container">
                    <div className="pass-table">
                        <div className="pass-table-header">
                            <Text
                                fontWeight="semibold"
                                className="pass-table-header-text"
                            >
                                All Pass Types
                            </Text>
                        </div>
                        <div className="pass-table-content">
                            <div className="pass-table-item">
                                <Icon
                                    iconName="check-circle"
                                    className="check"
                                    size="sm"
                                />
                                <Text fontWeight="normal" className="description">
                                    Proceeds from the pass sales used to improve & enhance
                                    visitor services
                                </Text>
                            </div>
                            <div className="pass-table-item">
                                <Icon
                                    iconName="check-circle"
                                    className="check"
                                    size="sm"
                                />

                                <Text fontWeight="normal" className="description">
                                    Non-refundable, non-transferable, cannot be replaced
                                    if lost or stolen
                                </Text>
                            </div>
                            <div className="pass-table-item">
                                <Icon
                                    iconName="check-circle"
                                    className="check"
                                    size="sm"
                                />

                                <Text fontWeight="normal" className="description">
                                    Digital pass available for instant download & use on
                                    phone or tablet
                                </Text>
                            </div>
                            <div className="pass-table-item">
                                <Icon
                                    iconName="check-circle"
                                    className="check"
                                    size="sm"
                                />

                                <Text fontWeight="semibold" className="description">
                                    Digital passes may not be purchased as gifts
                                </Text>
                            </div>
                        </div>
                    </div>
                    <div className="pass-table">
                        <div className="pass-table-header">
                            <Text
                                fontWeight="semibold"
                                className="pass-table-header-text"
                            >
                                Site Passes
                            </Text>
                        </div>
                        <div className="pass-table-content">
                            <div className="pass-table-item">
                                <Icon
                                    iconName="check-circle"
                                    className="check"
                                    size="sm"
                                />

                                <Text fontWeight="normal" className="description">
                                    Covers entrance or standard amenity (day-use) fees at
                                    a <strong>single</strong> federal recreation location
                                </Text>
                            </div>
                            <div className="pass-table-item">
                                <Icon
                                    iconName="check-circle"
                                    className="check"
                                    size="sm"
                                />

                                <Text fontWeight="normal" className="description">
                                    Price varies based on location and pass type (daily,
                                    weekly, seasonal, etc.)
                                </Text>
                            </div>
                        </div>
                    </div>
                    <div className="pass-table">
                        <div className="pass-table-header">
                            <Text
                                fontWeight="semibold"
                                className="pass-table-header-text"
                            >
                                America the Beautiful Passes
                            </Text>
                        </div>
                        <div className="pass-table-content">
                            <div className="pass-table-item">
                                <Icon
                                    iconName="check-circle"
                                    className="check"
                                    size="sm"
                                />

                                <Text fontWeight="normal" className="description">
                                    Covers entrance or standard amenity (day-use) fees at{' '}
                                    <strong>thousands</strong> of federal recreation
                                    locations
                                </Text>
                            </div>
                            <div className="pass-table-item">
                                <Icon
                                    iconName="check-circle"
                                    className="check"
                                    size="sm"
                                />

                                <Text fontWeight="normal" className="description">
                                    Price varies based on pass type (annual, military,
                                    senior, etc.); some passes are free or discounted
                                </Text>
                            </div>
                            <div className="pass-table-item">
                                <Icon
                                    iconName="check-circle"
                                    className="check"
                                    size="sm"
                                />

                                <Text fontWeight="normal" className="description">
                                    Senior and Access Passes provide discounts on some
                                    expanded amenity fees such as camping
                                </Text>
                            </div>
                        </div>
                    </div>
                </div>
                {wizardEnabled && (
                    <div className="which-pass-wizard-cta">
                        <Heading
                            headingLevel={2}
                            appearance="h6"
                            className="which-pass-wizard-cta__heading"
                        >
                            Still need help finding the right pass for you?
                        </Heading>
                        <Button onClick={openWizard}>Help Me Choose a Pass</Button>
                    </div>
                )}
            </section>
        </ContentContainerBodyShift>
    );
}
export default WhichPass;
